import {ActivePlacementsSyncJob, Placement_UI, RemotePlacement_UI} from './placements.types';
import {toLocalPlacement_UI} from './campaignPlacements.dataConverters';
import apiV2Service from '../api/api-v2.service';
import {ImportResponse} from '../../components/CLFileImportModal/CLFileImportModal';
import waitForJob from '../jobs/utils/waitForJob';
import {Payload} from '../api/apiTypes';
import {JobExecutionParams} from '../jobs/jobs.types';
import {AdSize} from '../adSize/adSize.types';
import {CampaignTrackerTypeName} from '../campaignTrackers/campaignTrackers.types';
import enumsService from '../enums/enums.service';
import {CLEnumType} from '../enums/enums.types';
import {dspApi} from '../dsp/dspApi';

const basePath = 'CampaignPlacements';

async function getPlacements({
  campaignId,
  campaignVersionId,
}: {
  campaignId: number;
  campaignVersionId?: number;
}): Promise<Placement_UI[]> {
  const [trackersCountByPlacement, {data: placements}, campaignEnums, dsps] = await Promise.all([
    getTrackersCountByPlacement({campaignId, campaignVersionId: campaignVersionId ?? 0}),
    apiV2Service.executeGetRequest<RemotePlacement_UI[]>(
      `${basePath}/campaign/${campaignId}/version/${campaignVersionId}/placement`,
    ),
    enumsService.loadEnums(CLEnumType.CampaignPage),
    dspApi.getAll({}),
  ]);

  return placements.map((plc) =>
    toLocalPlacement_UI(plc, trackersCountByPlacement, campaignEnums, dsps),
  );
}

async function importFromCsv({
  campaignId,
  campaignVersionId,
  deleteExisting,
  overrideExisting,
  file,
}: {
  campaignId: number;
  campaignVersionId?: number;
  file: File;
  deleteExisting: boolean;
  overrideExisting: boolean;
}) {
  const url = `${basePath}/ImportPlacements`;

  const {data} = await apiV2Service.uploadFile<ImportResponse>({
    endpoint: url,
    file: file,
    params: {
      campaignId,
      campaignVersionId,
      deleteAllExistingPlacementsPriorToImport: deleteExisting,
      overwriteExistingPlacementsData: overrideExisting,
    },
  });

  return data;
}

async function exportToCsv(params: {campaignId: number; campaignVersionId?: number}) {
  return apiV2Service.downloadFileRequest({
    endpoint: `${basePath}/ExportPlacements`,
    paramsObj: {
      ...params,
      fileExtension: 'csv',
    },
  });
}

function getImportPlacementsExampleFileUrl(campaignId: number) {
  return apiV2Service.downloadFileRequest({
    endpoint: `${basePath}/DownloadImportExportExampleFile`,
    paramsObj: {campaignId},
  });
}

function addPlacement_v2({
  campaignId,
  campaignVersionId,
  placement,
}: {
  campaignId: number;
  campaignVersionId?: number;
  placement: Placement_UI;
}) {
  return apiV2Service.executePutRequest<[]>(
    `${basePath}/campaign/${campaignId}/version/${campaignVersionId}/placement`,
    {},
    {...placement},
  );
}

function editPlacement_v2({
  campaignId,
  campaignVersionId,
  placement,
}: {
  campaignId: number;
  campaignVersionId?: number;
  placement: Placement_UI;
}) {
  return apiV2Service.executePutRequest<[]>(
    `${basePath}/campaign/${campaignId}/version/${campaignVersionId}/placement/${placement.id}`,
    {},
    {...placement},
  );
}

async function deletePlacement_v2({
  placementsIds,
  campaignVersionId,
  campaignId,
}: {
  campaignId: number;
  campaignVersionId?: number;
  placementsIds: number[];
}): Promise<Payload<void>> {
  return apiV2Service.executePostRequest(
    `${basePath}/campaign/${campaignId}/version/${campaignVersionId}/placement/batchDelete`,
    {},
    {placementIds: placementsIds},
  );
}

async function syncDspCreatives({
  placementsIds,
  campaignVersionId,
  campaignId,
}: {
  placementsIds: number[];
  campaignVersionId: number;
  campaignId: number;
}) {
  const {
    data: {executionId: jobId},
  } = await apiV2Service.executePostRequest<JobExecutionParams>(
    `${basePath}/campaign/${campaignId}/version/${campaignVersionId}/syncDspCreatives`,
    {},
    {placementIds: placementsIds},
  );

  //start jobly without waiting, in order for the placements table to refetch placements
  if (jobId > 0) waitForJob({jobId, jobName: 'Sync DSP Creatives'});

  return jobId;
}

async function pushTags({
  placementsIds,
  campaignVersionId,
  campaignId,
  publishAsPaused,
}: {
  placementsIds: number[];
  campaignVersionId: number;
  campaignId: number;
  publishAsPaused?: boolean;
}) {
  const payload: {placementIds: number[]; publishAsPaused?: boolean} = {
    placementIds: placementsIds,
  };

  if (publishAsPaused) {
    payload.publishAsPaused = publishAsPaused;
  }

  const {
    data: {executionId: jobId},
  } = await apiV2Service.executePostRequest<JobExecutionParams>(
    `${basePath}/campaign/${campaignId}/version/${campaignVersionId}/publishTags`,
    {},
    payload,
  );

  if (jobId > 0) waitForJob({jobId, jobName: 'Publish Tags'});

  return jobId;
}

async function setRequiresPingForAttribution({
  placementIds,
  campaignVersionId,
  campaignId,
}: {
  placementIds: number[];
  campaignVersionId: number;
  campaignId: number;
}): Promise<Payload<void>> {
  return apiV2Service.executePostRequest(
    `${basePath}/campaign/${campaignId}/version/${campaignVersionId}/setRequirePingForAttributionBatch`,
    {},
    {placementIds},
  );
}

async function activatePausedDSPCreatives({
  placementsIds,
  campaignVersionId,
  campaignId,
}: {
  placementsIds: number[];
  campaignVersionId: number;
  campaignId: number;
}) {
  const {
    data: {executionId: jobId},
  } = await apiV2Service.executePostRequest<JobExecutionParams>(
    `${basePath}/campaign/${campaignId}/version/${campaignVersionId}/activatePaused`,
    {},
    {placementIds: placementsIds},
  );

  if (jobId > 0) await waitForJob({jobId, jobName: 'Activate Paused DSP Creatives'});
}

async function getSyncJob({
  campaignVersionId,
  campaignId,
}: {
  campaignVersionId: number;
  campaignId: number;
}) {
  const {data: syncJob} = await apiV2Service.executeGetRequest<ActivePlacementsSyncJob>(
    `${basePath}/campaign/${campaignId}/version/${campaignVersionId}/activeSyncOrPublishJob`,
  );

  return syncJob;
}

function checkIfPlacementsRequiresMigration(campaignId: number, campaignVersionId: number) {
  return apiV2Service
    .executeGetRequest<boolean>(
      `${basePath}/campaign/${campaignId}/version/${campaignVersionId}/placement/requiresMigration`,
    )
    .then((res) => res.data);
}

function getSupportedAdSizes() {
  return apiV2Service
    .executeGetRequest<AdSize[]>(`${basePath}/GetSupportedAdsizes/supportedAdSizes`)
    .then((res) => res.data);
}

function getTrackersCountByPlacement({
  campaignId,
  campaignVersionId,
}: {
  campaignId: number;
  campaignVersionId: number;
}) {
  return apiV2Service
    .executeGetRequest<
      Record<number, Record<CampaignTrackerTypeName, number>>
    >(`${basePath}/campaign/${campaignId}/version/${campaignVersionId}/trackersCountPerType`)
    .then((res) => res.data);
}

async function getCampaignPlacementsCount({campaignId}: {campaignId: number}) {
  return apiV2Service.executeGetRequest<number>(
    `${basePath}/campaign/${campaignId}/latest/placement/count`,
  );
}

async function searchPlacements({
  campaignId,
  campaignVersionId,
  prompt,
}: {
  campaignId: number;
  campaignVersionId: number;
  prompt: string;
}) {
  const {data} = await apiV2Service.executeGetRequest<{
    filtersExplanation: string;
    placements: RemotePlacement_UI[];
  }>(`${basePath}/campaign/${campaignId}/version/${campaignVersionId}/placement/search`, {prompt});

  return data;
}

export const campaignPlacementsApi = {
  importFromCsv,
  exportToCsv,
  getImportPlacementsExampleFileUrl,
  getPlacements,
  getCampaignPlacementsCount,
  searchPlacements,

  addPlacement_v2,
  editPlacement_v2,
  deletePlacement_v2,
  syncDspCreatives,
  pushTags,
  setRequiresPingForAttribution,
  activatePausedDSPCreatives,
  getSyncJob,
  checkIfPlacementsRequiresMigration,

  getSupportedAdSizes,
};
